(() => {
  'use strict';
  if (customElements.get('jarhi-cart-drawer')) return;
  const baseFetch = window.fetch.bind(window);
  const base = window.Shopify?.routes?.root || '/';
  const mutations = /\/cart\/(add|change|update|clear)(?:\.js|\.json)?(?:\?|$)/;
  const current = () => document.querySelector('jarhi-cart-drawer');
  // Keep Shrine's product form and section response integration, while directing its drawer opens to the native Hebrew cart.
  for (const name of ['cart-drawer','cart-notification']) {
    customElements.whenDefined(name).then(() => {
      const component = customElements.get(name);
      const originalOpen = component.prototype.open;
      if (typeof originalOpen !== 'function') return;
      component.prototype.open = function(trigger) {
        const drawer = current();
        if (drawer?.config?.enabled) {
          if (trigger || drawer.config.open_on_add || !drawer.hidden) return drawer.open();
          return drawer.scheduleRefresh(false);
        }
        return originalOpen.apply(this, arguments);
      };
    });
  }

  const changed = (url, success) => {
    const cart = current();
    if (!cart?.config.enabled || !success || !mutations.test(String(url))) return;
    cart.scheduleRefresh(/\/cart\/add/.test(String(url)) && cart.config.open_on_add);
  };
  // Observe successful theme cart requests without changing their bodies or responses.
  window.fetch = function(...args) {
    const url = args[0]?.url || args[0];
    return baseFetch(...args).then(response => {
      changed(url, response.ok);
      return response;
    });
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    if (mutations.test(String(url))) {
      this.addEventListener('load', () => changed(url, this.status >= 200 && this.status < 300), {once:true});
    }
    return xhrOpen.call(this, method, url, ...args);
  };

  class JarhiCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.sequence = 0;
      this.busy = false;
      this.hidden = true;
      this.readConfig();
      this.chooseUpsells();
      if (!this.config.enabled) return;
      document.body.classList.add('jarhi-cart-active');
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => this.onChange(event));
      this.addEventListener('keydown', event => this.onKey(event));
      this.addEventListener('submit', event => {
        if (event.target.matches('[data-jarhi-checkout]')) {
          if (this.busy) event.preventDefault();
          else this.hideError();
        }
      });
      this.documentClick = event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || this.contains(event.target)) return;
        const link = event.target.closest('a[href], #cart-icon-bubble, [data-src="#dropdnMinicartPopup"]');
        if (!link) return;
        const path = link.getAttribute('href');
        const isCart = link.matches('#cart-icon-bubble, [data-src="#dropdnMinicartPopup"]') || (path && new URL(path, location.href).pathname.replace(/\/$/,'') === base.replace(/\/$/,'') + '/cart');
        if (!isCart) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open();
      };
      document.addEventListener('click', this.documentClick, true);
      this.documentSubmit = event => {
        const form=event.target;
        if(event.defaultPrevented || !form.matches('form') || this.contains(form)) return;
        const action=new URL(form.action,location.href);
        if(action.origin!==location.origin || !/\/cart\/add(?:\.js)?$/.test(action.pathname)) return;
        event.preventDefault();
        if(!this.busy)this.addFromForm(form,event.submitter);
      };
      document.addEventListener('submit',this.documentSubmit);
      this.sectionSelect = event => {
        if (event.target.contains(this)) this.open(false);
      };
      document.addEventListener('shopify:section:select', this.sectionSelect);
      this.blockSelect = event => {
        if (this.contains(event.target)) this.open(false);
      };
      document.addEventListener('shopify:block:select', this.blockSelect);
      if (window.Shopify?.designMode && this.config.editor_preview) this.open(false);
    }

    disconnectedCallback() {
      clearTimeout(this.refreshTimer);
      ++this.sequence;
      document.removeEventListener('click', this.documentClick, true);
      document.removeEventListener('submit',this.documentSubmit);
      document.removeEventListener('shopify:section:select', this.sectionSelect);
      document.removeEventListener('shopify:block:select', this.blockSelect);
      if (!this.hidden) {
        document.body.style.overflow = this.oldOverflow || '';
        for (const element of this.inerted || []) element.inert = false;
      }
      document.body.classList.remove('jarhi-cart-active');
    }

    readConfig() {
      this.config = JSON.parse(this.querySelector('[data-jarhi-config]').textContent);
    }

    chooseUpsells() {
      const target = this.querySelector('[data-jarhi-upsell-list]');
      if (!target) return;
      target.replaceChildren();
      let rules = [...this.querySelectorAll('template[data-jarhi-rule][data-match="true"]')];
      const specific = rules.filter(rule => rule.dataset.fallback !== 'true');
      if (this.config.rule_mode === 'first') rules = specific.length ? specific.slice(0,1) : rules.slice(0,1);
      else rules = rules.filter(rule => rule.dataset.fallback !== 'true' || !specific.length);
      const seen = new Set();
      let candidates = [];
      for (const rule of rules) {
        for (const card of rule.content.querySelectorAll('[data-jarhi-product]')) {
          const id = card.dataset.jarhiProduct;
          if (!seen.has(id)) { seen.add(id); candidates.push(card); }
        }
      }
      // Collection order is stable by default. Random mode is an explicit merchant choice.
      if (this.config.selection === 'random') {
        for (let i=candidates.length-1; i>0; i--) {
          const j = Math.floor(Math.random()*(i+1));
          [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
        }
      }
      for (const card of candidates.slice(0,this.config.upsell_limit)) target.append(card.cloneNode(true));
      this.querySelector('[data-jarhi-upsell-module]').hidden = !target.children.length;
    }

    async open(update=true) {
      if (!this.config.enabled) return;
      if (this.hidden) {
        this.returnFocus = document.activeElement;
        this.oldOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.inerted = [];
        for (const element of document.body.children) {
          if (element.contains(this) || ['SCRIPT','STYLE','LINK'].includes(element.tagName) || element.inert) continue;
          element.inert = true;
          this.inerted.push(element);
        }
        this.hidden = false;
        this.querySelector('[data-jarhi-close]').focus();
      }
      if (update && !this.busy) {
        this.setBusy(true);
        try { await this.refresh(); } catch (error) { this.showError(this.config.error_text); }
        finally { this.setBusy(false); }
      }
    }

    close() {
      if (this.busy) return;
      this.hidden = true;
      document.body.style.overflow = this.oldOverflow || '';
      for (const element of this.inerted || []) element.inert = false;
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
    }

    onKey(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      if (event.key !== 'Tab') return;
      const focusable = [...this.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled])')].filter(el => el.getClientRects().length && !el.closest('[hidden]'));
      const first=focusable[0], last=focusable.at(-1);
      if (!first) return;
      if (event.shiftKey && document.activeElement===first) {event.preventDefault();last.focus();}
      else if (!event.shiftKey && document.activeElement===last) {event.preventDefault();first.focus();}
    }

    onClick(event) {
      const button = event.target.closest('[data-jarhi-action]');
      if (!button) return;
      const action = button.dataset.jarhiAction;
      event.preventDefault();
      if (action === 'close') return this.close();
      if (this.busy) return;
      const item=button.closest('[data-jarhi-line]');
      if (action === 'remove' || action === 'minus' || action === 'plus') {
        const value=Number(item.querySelector('[data-jarhi-quantity]').value);
        const quantity=action==='remove'?0:Math.max(0,value+(action==='plus'?1:-1));
        this.mutate('change', {id:item.dataset.jarhiLine,quantity}, item.dataset.jarhiLine);
      }
      if (action === 'add') {
        const card=button.closest('[data-jarhi-product]');
        const variant=card.querySelector('select')?.value || button.dataset.variant;
        this.mutate('add', {items:[{id:Number(variant),quantity:1}]});
      }
    }

    onChange(event) {
      const field=event.target;
      if (field.matches('[data-jarhi-quantity]')) {
        const quantity=Number(field.value);
        if (!Number.isSafeInteger(quantity) || quantity<0) {field.value=field.dataset.previous;return;}
        if (this.busy) return;
        this.mutate('change',{id:field.closest('[data-jarhi-line]').dataset.jarhiLine,quantity},field.closest('[data-jarhi-line]').dataset.jarhiLine);
      }
      if (field.matches('[data-jarhi-variant]')) {
        const option=field.selectedOptions[0];
        const price=field.closest('[data-jarhi-product]').querySelector('[data-jarhi-offer-price]');
        price.replaceChildren();
        if (option.dataset.compare) {const compare=document.createElement('s');compare.textContent=option.dataset.compare;price.append(compare);}
        const amount=document.createElement('span');amount.textContent=option.dataset.price;price.append(amount);
      }
      if (field.matches('[data-jarhi-note]') && !this.busy) this.mutate('update',{note:field.value});
    }

    setBusy(busy) {
      this.busy=busy;
      this.querySelector('.jarhi-cart-panel').setAttribute('aria-busy',String(busy));
      for (const field of this.querySelectorAll('button,input,textarea,select')) if(field.dataset.jarhiAction!=='close') field.disabled=busy;
      this.querySelector('[data-jarhi-checkout-button]')?.setAttribute('aria-disabled',String(busy));
    }

    async addFromForm(form,submitter) {
      this.setBusy(true);
      this.hideError();
      const disabled=submitter?.disabled;
      if(submitter)submitter.disabled=true;
      const payload=new FormData(form);
      if(submitter?.name)payload.append(submitter.name,submitter.value);
      let succeeded=false;
      try {
        const response=await baseFetch(base+'cart/add.js',{method:'POST',headers:{'Accept':'application/json'},body:payload});
        const data=await response.json();
        if(!response.ok)throw new Error(data.description || data.message || this.config.error_text);
        succeeded=true;
        await this.refresh();
        if(this.config.open_on_add)await this.open(false);
        this.querySelector('[data-jarhi-status]').textContent=this.config.updated_text;
      } catch(error) {
        if(!succeeded) {try {await this.refresh();} catch(_) {}}
        await this.open(false);
        this.showError(succeeded?this.config.refresh_error:(error.message || this.config.error_text));
      } finally {
        this.setBusy(false);
        if(submitter)submitter.disabled=disabled;
      }
    }

    async mutate(operation,payload,focusKey) {
      if (this.busy) return;
      this.setBusy(true);
      this.hideError();
      let succeeded=false;
      try {
        const response=await baseFetch(base+'cart/'+operation+'.js',{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify(payload)});
        const data=await response.json();
        if (!response.ok) throw new Error(data.description || data.message || this.config.error_text);
        succeeded=true;
        await this.refresh(focusKey);
        this.querySelector('[data-jarhi-status]').textContent=this.config.updated_text;
      } catch(error) {
        // Shopify can partially accept a quantity limited by stock; resync without repeating the mutation.
        if(!succeeded) {try {await this.refresh(focusKey);} catch(_) {}}
        // A successful mutation must never be repeated when only the rendering request failed.
        this.showError(succeeded?this.config.refresh_error:(error.message || this.config.error_text));
      } finally {
        this.setBusy(false);
      }
    }

    scheduleRefresh(open) {
      this.openAfterRefresh=this.openAfterRefresh || open;
      clearTimeout(this.refreshTimer);
      this.refreshTimer=setTimeout(async()=>{
        if (this.busy) return this.scheduleRefresh(this.openAfterRefresh);
        const shouldOpen=this.openAfterRefresh;
        this.openAfterRefresh=false;
        try {await this.refresh();if(shouldOpen)await this.open(false);}catch(error){if(!this.hidden)this.showError(this.config.error_text);}
      },100);
    }

    async refresh(focusKey) {
      const sequence=++this.sequence;
      const url=new URL(base,location.origin);
      url.searchParams.set('section_id',this.config.section_id);
      const response=await baseFetch(url.toString(),{headers:{'Accept':'text/html'},cache:'no-store'});
      if(!response.ok)throw new Error('Section request failed');
      const html=await response.text();
      const rendered=new DOMParser().parseFromString(html,'text/html').querySelector('jarhi-cart-drawer');
      if(!rendered)throw new Error('Cart section missing');
      if(sequence!==this.sequence)return;
      const scroll=this.querySelector('.jarhi-cart-scroll').scrollTop;
      const active=document.activeElement;
      const closeFocused=active?.hasAttribute('data-jarhi-close');
      this.innerHTML=rendered.innerHTML;
      this.style.cssText=rendered.style.cssText;
      this.dir=rendered.dir;
      this.dataset.side=rendered.dataset.side;
      this.readConfig();
      this.chooseUpsells();
      this.querySelector('.jarhi-cart-scroll').scrollTop=scroll;
      for(const badge of document.querySelectorAll('.minicart-qty')) badge.textContent=String(this.config.item_count);
      const count = this.config.item_count;
      const icon = document.getElementById('cart-icon-bubble');
      let bubble = icon?.querySelector('.cart-count-bubble');
      if (!count) bubble?.remove();
      else if (icon) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          const visible = document.createElement('span');
          visible.setAttribute('aria-hidden','true');
          const accessible = document.createElement('span');
          accessible.className = 'visually-hidden';
          bubble.append(visible,accessible);
          icon.append(bubble);
        }
        for (const label of bubble.querySelectorAll('span')) label.textContent = String(count);
      }

      if(this.busy)this.setBusy(true);
      // Restore focus after replacing the section's HTML.
      if(!this.hidden){
        const replacement=focusKey?[...this.querySelectorAll('[data-jarhi-line]')].find(line=>line.dataset.jarhiLine===focusKey)?.querySelector('[data-jarhi-quantity]'):null;
        if(replacement){replacement.disabled=false;replacement.focus();}
        else if(closeFocused || this.contains(active) || !active?.isConnected)this.querySelector('[data-jarhi-close]').focus();
      }
    }

    hideError(){const target=this.querySelector('[data-jarhi-error]');target.hidden=true;target.textContent='';}
    showError(message){const target=this.querySelector('[data-jarhi-error]');target.textContent=message;target.hidden=false;}
  }
  customElements.define('jarhi-cart-drawer',JarhiCart);
})();
